import React, { useState } from 'react';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import './TopCancerCost.scss';

/**
 * 「醫治香港頭5大癌症的平均費用」, an in-house replacement for the Piktochart of the same name, in the
 * look of ai.portfoplus.com: the Piktochart's five bars, and a monthly-saving slider that turns the
 * picked cancer's cost into the years it would take to save for it. The figures are the Piktochart's
 * own ("from the internet, for reference only"), kept as they are.
 */

type Cancer = 'colorectal' | 'lung' | 'breast' | 'prostate' | 'liver';

/** Average cost of treatment, HKD; the Piktochart's figures. */
export const COST: Record<Cancer, number> = {
  colorectal: 600000,
  lung: 650000,
  breast: 550000,
  prostate: 400000,
  liver: 550000
};
const CANCERS: Cancer[] = ['colorectal', 'lung', 'breast', 'prostate', 'liver'];
const AVERAGE = 600000;
const MAX = 700000;

const SAVE_MIN = 1000;
const SAVE_MAX = 10000;
const SAVE_STEP = 500;
const SAVE_DEFAULT = 3000;

/** Years of saving `monthly` to reach `cost`, to one decimal. */
export const yearsToSave = (cost: number, monthly: number) => Math.round((cost / monthly / 12) * 10) / 10;

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const TopCancerCost = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [picked, setPicked] = useState<Cancer>('lung');
  const [monthly, setMonthly] = useState<number>(SAVE_DEFAULT);

  /** 600000 as "60萬" in Chinese, "600K" in English. */
  const short = (n: number) => t('tcc-short', { n: intl.locale === 'en' ? n / 1000 : n / 10000 });
  const money = (n: number) => n.toLocaleString('en-US');

  return (
    <div className="top-cancer-cost" lang={HTML_LANG[intl.locale] || intl.locale}>
      <header className="tcc-head">
        <h2 className="tcc-title">
          <span>{t('tcc-title')}</span>
        </h2>
        <p className="tcc-sub">{t('tcc-sub')}</p>
      </header>

      <div className="tcc-chart">
        <div className="tcc-avg" style={{ '--at': AVERAGE / MAX } as React.CSSProperties}>
          <span>{t('tcc-avg-line', { v: short(AVERAGE) })}</span>
        </div>
        {CANCERS.map(cancer => (
          <button
            key={cancer}
            type="button"
            className={`tcc-bar is-${cancer}${cancer === picked ? ' is-on' : ''}`}
            aria-pressed={cancer === picked}
            onClick={() => setPicked(cancer)}
          >
            <span className="tcc-col">
              <span className="tcc-fill" style={{ height: `${(COST[cancer] / MAX) * 100}%` }}>
                <strong>{short(COST[cancer])}</strong>
              </span>
            </span>
            <span className="tcc-name">{t(`tcc-${cancer}`)}</span>
          </button>
        ))}
      </div>

      <section className="tcc-try" aria-live="polite">
        <label htmlFor="tcc-monthly">
          <span>
            <SavingsRoundedIcon aria-hidden="true" />
            {t('tcc-try')}
          </span>
          <strong>HKD {money(monthly)}</strong>
        </label>
        <input
          id="tcc-monthly"
          type="range"
          min={SAVE_MIN}
          max={SAVE_MAX}
          step={SAVE_STEP}
          value={monthly}
          onChange={event => setMonthly(Number(event.target.value))}
          style={{ '--fill': `${((monthly - SAVE_MIN) / (SAVE_MAX - SAVE_MIN)) * 100}%` } as React.CSSProperties}
        />
        <p className="tcc-result">
          {t('tcc-result', {
            cancer: t(`tcc-${picked}`),
            cost: money(COST[picked]),
            years: yearsToSave(COST[picked], monthly)
          })}
        </p>
        <small>{t('tcc-try-note')}</small>
      </section>

      <p className="tcc-average">{t('tcc-average', { v: money(AVERAGE) })}</p>

      <div className="tcc-tip">
        <CampaignRoundedIcon aria-hidden="true" />
        <p>{t('tcc-conclusion')}</p>
      </div>

      <p className="tcc-source">{t('tcc-source')}</p>
    </div>
  );
};

export default TopCancerCost;
